// Outbound-notification request shapes — the bodies the console POSTs and the
// Worker validates. Unlike the rest of this package (response shapes), these are
// REQUEST contracts: the value of declaring them once is that a field the Worker
// requires can't quietly go missing from a caller.
//
// Why this file exists: five console surfaces build the invite body (Client
// Admin, Zone Team, Parkify Admin, the onboarding wizard, and Avery's
// invite_team_member proposal), and one of them omitted `invitedBy`. Every
// AI-driven invite 400'd, a fire-and-forget `.catch()` swallowed it, and the
// operator watched the membership save and assumed the person had been emailed.
// Nobody had. A required field on a shared type fails at compile time instead.

/**
 * POST /api/notifications/invite — the console-invite / partner-welcome email.
 *
 * Handled by parkify-pay/api/src/notifications/invite.ts (sendInviteEmail).
 */
export interface InviteRequest {
  /**
   * Recipient. Exactly one address — the Worker rejects an array or object
   * here, because Resend answers those with a 422 that reads like an outage.
   */
  email: string
  /**
   * Selects the TEMPLATE: 'owner' sends the partner welcome, anything else the
   * standard console invite. A client-scoped caller doesn't get to choose — the
   * route overwrites this from the stored user record.
   */
  role: string
  /**
   * Who is doing the inviting, as a person: rendered in the subject line
   * ("X invited you to Parkify") and stored as `actorEmail` on the resulting
   * invite.sent / invite.failed audit event. This is the approving operator,
   * NOT the surface that happened to fire the request — an Avery-proposed
   * invite is still attributed to the human who approved it, matching how that
   * proposal's own audit entry attributes the actor.
   */
  invitedBy: string
  /** Invitee's display name. Falls back to `invitedBy` in the owner template. */
  name?: string
  /** Tenant shown in the email body — which Parkify org they're joining. */
  companyName?: string
  /** Tenant scope for the audit entry. null/absent = a Parkify-staff invite. */
  clientId?: string | null
}

/**
 * The fields sendInviteEmail rejects the request without (400).
 *
 * Exported as a runtime value on purpose: the Worker's guard filters on this
 * list and the console's contract tests iterate it, so adding a requirement
 * here breaks every caller that doesn't supply it — loudly, in CI — rather than
 * turning into another silently-swallowed 400.
 */
export const REQUIRED_INVITE_FIELDS = ['email', 'role', 'invitedBy'] as const

export type RequiredInviteField = typeof REQUIRED_INVITE_FIELDS[number]
